// An image upload — a venue photo, the venue's logo, a menu product's picture — the way #403 rule 3 and
// spec/08 §4.19 describe: reserve the file, PUT the bytes straight to object storage with the signed URL,
// complete it, and attach it by file id with the next save (the venue's, or the product's). The bytes never
// pass through the api.
//
// Which image types come from the contract's `CreateFile` (vite/contract-schemas.ts), and how large each
// purpose may be from `@clavian/contracts/constants`, so the console refuses exactly what the server would,
// before a byte is sent.

import type { ApiClient, ImageContentType, VenuePhoto } from '@clavian/api-client';
import { MAX_IMAGE_UPLOAD_BYTES, MAX_LOGO_UPLOAD_BYTES, MAX_PRODUCT_IMAGE_UPLOAD_BYTES } from '@clavian/contracts/constants';
import { $defs } from 'virtual:clavian/contract-schemas';

export type UploadFailure = 'wrongType' | 'tooLarge' | 'failed';

export class PhotoUploadError extends Error {
  override readonly name = 'PhotoUploadError';
  readonly reason: UploadFailure;
  constructor(reason: UploadFailure) {
    super(reason);
    this.reason = reason;
  }
}

/** A photo that is there. (The generated `VenuePhoto` includes `null`, for the cover that may be absent.) */
export type Photo = NonNullable<VenuePhoto>;

const imageTypes = (($defs['ImageContentType']?.['enum'] ?? []) as string[]).filter((type) => type !== '');
const maxBytes = ((($defs['CreateFile']?.['properties'] ?? {}) as Record<string, Record<string, unknown>>)['bytes']?.['maximum'] ??
  Number.POSITIVE_INFINITY) as number;

export const PHOTO_TYPES: readonly string[] = imageTypes;
export const PHOTO_MAX_BYTES: number = maxBytes;

/** What an image is for, and — for a venue's — which venue. The contract's `CreateFile` purposes. */
export type ImageTarget =
  | { readonly purpose: 'venue_photo' | 'venue_logo'; readonly venueId: string }
  | { readonly purpose: 'product_image' };

/** The most one upload of each purpose may be (the contract's `MAX_UPLOAD_BYTES`). */
export const IMAGE_MAX_BYTES: Readonly<Record<ImageTarget['purpose'], number>> = {
  venue_photo: Math.min(MAX_IMAGE_UPLOAD_BYTES, maxBytes),
  venue_logo: MAX_LOGO_UPLOAD_BYTES,
  product_image: MAX_PRODUCT_IMAGE_UPLOAD_BYTES,
};

/** How the bytes are sent. A test passes its own; the browser's `fetch` otherwise. */
export type PutBytes = (url: string, init: { method: 'PUT'; headers: Record<string, string>; body: Blob }) => Promise<{ ok: boolean }>;

export async function uploadVenuePhoto(
  api: ApiClient,
  organizationId: string,
  venueId: string,
  file: File,
  put: PutBytes = (url, init) => fetch(url, init),
): Promise<Photo> {
  return uploadImage(api, organizationId, { purpose: 'venue_photo', venueId }, file, put);
}

/** Upload an image for `target` and return it completed, ready to attach by its file id. */
export async function uploadImage(
  api: ApiClient,
  organizationId: string,
  target: ImageTarget,
  file: File,
  put: PutBytes = (url, init) => fetch(url, init),
): Promise<Photo> {
  if (!imageTypes.includes(file.type)) throw new PhotoUploadError('wrongType');
  if (file.size > IMAGE_MAX_BYTES[target.purpose] || file.size === 0) throw new PhotoUploadError(file.size === 0 ? 'failed' : 'tooLarge');

  const reserved = await api.createFile({
    path: { organizationId },
    body: {
      purpose: target.purpose,
      ...('venueId' in target ? { venueId: target.venueId } : {}),
      contentType: file.type as ImageContentType,
      bytes: file.size,
    },
  });
  // Exactly the Content-Type the signature covers, and the body whose length it covers (spec/08 §4.19).
  const sent = await put(reserved.upload.url, {
    method: 'PUT',
    headers: { 'content-type': reserved.upload.contentType },
    body: file,
  }).catch(() => ({ ok: false }));
  if (!sent.ok) throw new PhotoUploadError('failed');

  const completed = await api.completeFile({ path: { organizationId, fileId: reserved.file.id } });
  return { fileId: completed.id, url: completed.url, variants: completed.variants };
}

/** The file again, once the worker may have made its smaller sizes (spec/03 §7.4). */
export async function photoWhenReady(
  api: ApiClient,
  organizationId: string,
  fileId: string,
  options: { readonly attempts?: number; readonly intervalMs?: number; readonly signal?: AbortSignal } = {},
): Promise<Photo | undefined> {
  const attempts = options.attempts ?? 10;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (options.signal?.aborted) return undefined;
    const file = await api.getFile({ path: { organizationId, fileId } });
    if (file.status === 'ready' || file.status === 'failed') return { fileId: file.id, url: file.url, variants: file.variants };
    await new Promise((resolve) => setTimeout(resolve, options.intervalMs ?? 2000));
  }
  return undefined;
}
